/* eslint-disable no-cond-assign */
import get from 'lodash/get';
import moment from 'moment';

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints, DataPointResponse, PercentGrowthHasDisclaimer, RiskMetricHasDisclaimer } from '../DataPoints';
import { formatMessageWithLocale } from '../../../../utils/i18n';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import {
  CurrencyCell,
  NotApplicableCell,
  LipperScoreCell,
  GrowthCell,
  PercentageCell,
  StatCell,
  TextCell,
  NoDataCell,
  DateCell,
} from '../../../../views/components/cells';
import { CURRENCY } from '../../../../utils/currency';
import { MERBreakdown } from '../constants';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../../utils/permissions';
import Dictionary from '@/types/dictionary';
import SeriesData from '@/types/series-data';
import LocalizedString from '@/types/localized-string';
import GrowthTypes from '@/types/growth-types';
import RiskPeriods from '@/types/risk-periods';
import MinInvestmentInfo from '@/types/min-investment-info';
import LipperLeaders from '@/types/lipper-leaders';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { TIME_PERIODS } from '@/utils/constants';

export default class OverviewDAO extends ComparisonItemBasedProposalDAO {
  showSize: boolean | null = null;

  showBeta: boolean | null = null;

  showMER: boolean | null = null;

  showMERAsTotalCost: boolean | null = null;

  showMERBreakdown: boolean | null = null;

  showTax: boolean | null = null;

  showCommonInception: boolean | null = null;

  customTypes: Dictionary<LocalizedString> | null = null;

  showMinInvestment: boolean | null = null;

  showLipperScores: boolean | null = null;

  growthPeriods: Array<GrowthTypes> = ['YT', '6M', '1Y', '3Y', '5Y', '10Y'];

  stdDevPeriods: Array<RiskPeriods> = [];

  showTotalCost: boolean | null = null;

  forcedInceptionDateOnBenchmarks: number | null = null;

  showInceptionDate: boolean | null = null;

  sinceInceptionDisclaimerCounter: number = 0;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new OverviewDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const daoConfig: ProposalNS.OverviewConfig = this.proposalElement.DAO as ProposalNS.OverviewConfig;
    if (daoConfig.showSize !== undefined) {
      this.showSize = daoConfig.showSize;
    }

    if (daoConfig.customTypes !== undefined) {
      this.customTypes = daoConfig.customTypes;
    }

    if (daoConfig.showBeta !== undefined) {
      this.showBeta = daoConfig.showBeta;
    }

    if (daoConfig.showMER !== undefined) {
      this.showMER = daoConfig.showMER;
    }

    if (daoConfig.showMERBreakdown !== undefined) {
      this.showMERBreakdown = daoConfig.showMERBreakdown;
    }

    if (daoConfig.showMERAsTotalCost !== undefined) {
      this.showMERAsTotalCost = daoConfig.showMERAsTotalCost;
    }

    if (daoConfig.showTax !== undefined) {
      this.showTax = daoConfig.showTax;
    }

    if (daoConfig.showLipperScores !== undefined) {
      this.showLipperScores = daoConfig.showLipperScores;
    }

    if (daoConfig.showMinInvestment !== undefined) {
      this.showMinInvestment = daoConfig.showMinInvestment;
    }

    if (daoConfig.growthPeriods !== undefined) {
      this.growthPeriods = daoConfig.growthPeriods;
    }

    if (daoConfig.stdDevPeriods !== undefined) {
      this.stdDevPeriods = daoConfig.stdDevPeriods;
    }

    if (daoConfig.forcedInceptionDateOnBenchmarks !== undefined) {
      this.forcedInceptionDateOnBenchmarks = daoConfig.forcedInceptionDateOnBenchmarks;
    }

    if (daoConfig.showInceptionDate !== undefined) {
      this.showInceptionDate = daoConfig.showInceptionDate;
    }

    if (daoConfig.showTotalCost !== undefined) {
      this.showTotalCost = daoConfig.showTotalCost;
    }

    if (!userHasPermissionEnabled(this.proposal.appUser, PERMISSIONS.COMMON_INCEPTION) || this.proposal.items.length <= 1) {
      this.growthPeriods = this.growthPeriods.filter(p => p !== TIME_PERIODS.CI);
    }

    if (daoConfig.showCommonInception !== undefined) {
      this.showCommonInception = daoConfig.showCommonInception;
      this.growthPeriods = [...this.growthPeriods, 'CI'];
    }

    const { locale } = this.proposal.options;

    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      if (this.showSize) {
        const currency: string | undefined = itemDAO.item.currency === CURRENCY.MIXED ? CURRENCY.CAD : itemDAO.item.currency;
        const size: number = itemDAO.item.size !== undefined ? itemDAO.item.size : 0;
        const field: ProposalFieldDAO = new ProposalFieldDAO(
          formatMessageWithLocale('Proposal.Modules.Overview.Size', locale),
          { value: size, currency },
          ProposalStyleClassNames.ProposalTableContent,
        );
        field.cellType = CurrencyCell;
        this.addFieldDAO(itemDAO, field);

        this.addFieldName(formatMessageWithLocale('Proposal.Modules.Overview.Size', locale), ProposalStyleClassNames.ProposalTableContent);
      }

      let forcedInceptionDate: number | null = null;
      if (this.forcedInceptionDateOnBenchmarks != null) {
        const inceptionDate: Date = get(this.proposal, this.forcedInceptionDateOnBenchmarks);
        forcedInceptionDate = moment(inceptionDate, 'MM/DD/YYYY').valueOf();
      }

      if (this.showInceptionDate) {
        const inceptionDate = itemDAO.item.dates.inception?.toString();

        const label = this.formatMessage('Proposal.Modules.Overview.InceptionDate');
        if (inceptionDate) {
          this.addItemField(itemDAO, label, inceptionDate, DateCell);
        } else {
          this.addItemField(itemDAO, label, null, NoDataCell);
        }
      }

      const growthData: DataPointResponse<PercentGrowthHasDisclaimer> = DataPoints.getPercentGrowths(
        itemDAO.item,
        this.proposal.showBaseCurrencies === true,
        forcedInceptionDate && itemDAO.item.type === ProposalNS.ProposalItemType.Benchmark ? forcedInceptionDate : undefined,
      );

      const riskMetrics: DataPointResponse<Dictionary<RiskMetricHasDisclaimer>> = DataPoints.getRiskMetrics(
        itemDAO.item,
        this.proposal.showBaseCurrencies === true,
        forcedInceptionDate && itemDAO.item.type === ProposalNS.ProposalItemType.Benchmark ? forcedInceptionDate : undefined,
      );

      this.addDisclaimerKeys(growthData.disclaimerKeys, itemDAO.item);

      // I really see no way to make this nice at the moment. It is so specific to IG.
      if (this.customTypes) {
        let label;

        if (itemDAO.item.flags != null && Array.isArray(itemDAO.item.flags)) {
          for (const flag of itemDAO.item.flags) {
            if (flag.startsWith('customType-')) {
              label = this.customTypes[flag] != null ? this.formatLocalizedString(this.customTypes[flag]) : '';
            }
            break;
          }
        }
        this.addItemField(itemDAO, formatMessageWithLocale('Proposal.Modules.Overview.ProductType', locale), label, TextCell);
      }

      for (const growthPeriod of this.growthPeriods) {
        const fieldName = formatMessageWithLocale(`Proposal.Modules.Overview.${growthPeriod}`, locale);
        const value = growthData.response[growthPeriod] && growthData.response[growthPeriod].value;
        if (value != null) {
          const showSinceInceptionDisclaimer = growthPeriod === TIME_PERIODS.INC;
          const showMaxCommonDisclaimer = growthPeriod === TIME_PERIODS.CI;

          let disclaimerNumber: number | undefined;
          let disclaimerKey: string | undefined;
          let asteriskDisclaimerKey: string | undefined;
          let sectionKey: string | undefined;
          if (showSinceInceptionDisclaimer) {
            this.sinceInceptionDisclaimerCounter++;
            disclaimerNumber = this.sinceInceptionDisclaimerCounter;
            disclaimerKey = PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE;
            sectionKey = 'TRAILING_RETURNS_0';
          }

          if (growthData.response[growthPeriod].hasDisclaimer) {
            asteriskDisclaimerKey = PROPOSAL_DISCLAIMERS.SI_YOUNG_HOLDINGS;
            sectionKey = 'TRAILING_RETURNS_0';
          }

          // Pass both keys to the cell
          this.addItemField(
            itemDAO,
            fieldName,
            value,
            GrowthCell,
            growthData.response[growthPeriod].hasDisclaimer,
            disclaimerNumber,
            disclaimerKey,
            sectionKey,
            asteriskDisclaimerKey,
          );

          if (showSinceInceptionDisclaimer || showMaxCommonDisclaimer || growthData.response[growthPeriod].hasDisclaimer) {
            this.addDisclaimerKeys(
              [
                ...(showSinceInceptionDisclaimer ? [PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE] : []),
                ...(showMaxCommonDisclaimer ? [PROPOSAL_DISCLAIMERS.COMMON_INCEPTION_DATE] : []),
                ...(growthData.response[growthPeriod].hasDisclaimer ? [PROPOSAL_DISCLAIMERS.SI_YOUNG_HOLDINGS] : []),
              ],
              itemDAO.item,
            );
          }
        } else {
          this.addItemField(itemDAO, fieldName, null, NoDataCell);
        }
      }

      for (const stdDevPeriod of this.stdDevPeriods) {
        const fieldName = `
          ${formatMessageWithLocale('Proposal.Modules.Overview.StdDev', locale)} 
           - 
          ${formatMessageWithLocale(`Proposal.Modules.Overview.StdDevPeriods.${stdDevPeriod}`, locale)}
        `;
        const stdDev = riskMetrics.response.standardDeviation[stdDevPeriod];

        this.addItemField(itemDAO, fieldName, stdDev.value, StatCell, stdDev.hasDisclaimer);
      }

      if (this.showBeta) {
        let beta: number | undefined;

        let hasDisclaimer = false;
        if (riskMetrics.response && riskMetrics.response.beta && riskMetrics.response.beta['3Y']) {
          const riskMetric = riskMetrics.response.beta['3Y'];
          beta = riskMetric != null && riskMetric.value != null ? riskMetric.value : undefined;
          this.addDisclaimerKeys(riskMetrics.disclaimerKeys, itemDAO.item);
          hasDisclaimer = riskMetric != null && riskMetric.value != null && riskMetric.hasDisclaimer === true;
        }

        this.addItemField(itemDAO, formatMessageWithLocale('RiskMetrics.Beta', locale), beta, StatCell, hasDisclaimer);
      }

      if (this.showMER) {
        const merResponse: DataPointResponse<MERBreakdown> = DataPoints.getMer(itemDAO.item);
        this.addDisclaimerKeys(merResponse.disclaimerKeys, itemDAO.item);
        let label = this.formatMessage('Proposal.Modules.Overview.MER');
        if (this.showMERAsTotalCost || userHasPermissionEnabled(this.proposal.appUser, PERMISSIONS.TAXES)) {
          label = this.formatMessage('Proposal.Modules.Overview.TotalCost');
        }

        this.addItemField(itemDAO, label, merResponse.response.total, PercentageCell);
      }

      if (this.showMERBreakdown) {
        const merResponse: DataPointResponse<MERBreakdown> = DataPoints.getMer(itemDAO.item);
        this.addDisclaimerKeys(merResponse.disclaimerKeys, itemDAO.item);
        this.addItemField(itemDAO, formatMessageWithLocale('Proposal.Modules.Overview.MER', locale), merResponse.response.mer, PercentageCell);
        this.addItemField(itemDAO, formatMessageWithLocale('Proposal.Modules.Overview.AdvisoryFee', locale), merResponse.response.fee, PercentageCell);
        if (this.showTax) {
          this.addItemField(itemDAO, formatMessageWithLocale('Proposal.Modules.Overview.Tax', locale), merResponse.response.tax, PercentageCell);
        }
        this.addItemField(itemDAO, formatMessageWithLocale('Proposal.Modules.Overview.TotalCost', locale), merResponse.response.total, PercentageCell);
      }

      if (this.showTotalCost && !this.showMERBreakdown && !this.showMER) {
        const merResponse: DataPointResponse<MERBreakdown> = DataPoints.getMer(itemDAO.item);
        this.addItemField(itemDAO, formatMessageWithLocale('Proposal.Modules.Overview.TotalCost', locale), merResponse.response.total, PercentageCell);
      }

      if (this.showMinInvestment) {
        const minInvestmentResponse: DataPointResponse<MinInvestmentInfo | undefined> = DataPoints.getMinInvestmentInfo(itemDAO.item);
        this.addItemField(
          itemDAO,
          formatMessageWithLocale('Proposal.Modules.Overview.MinInvestment', locale),
          (minInvestmentResponse && minInvestmentResponse.response) || null,
          CurrencyCell,
        );
      }

      if (this.showLipperScores) {
        const labels: string[] = [
          formatMessageWithLocale('LipperLeaderScores.ConsistentReturn', this.locale),
          formatMessageWithLocale('LipperLeaderScores.TotalReturn', this.locale),
          formatMessageWithLocale('LipperLeaderScores.Preservation', this.locale),
        ];

        for (const label of labels) {
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        const lipperLeaderScores: DataPointResponse<LipperLeaders | undefined> = DataPoints.getLipperLeaderScores(itemDAO.item);

        let values: Array<number | undefined> = [];

        if (lipperLeaderScores.response !== undefined) {
          this.addDisclaimerKeys(lipperLeaderScores.disclaimerKeys, itemDAO.item);

          values = [
            lipperLeaderScores.response.consistentReturn !== undefined ? lipperLeaderScores.response.consistentReturn.overall.value : undefined,
            lipperLeaderScores.response.totalReturn !== undefined ? lipperLeaderScores.response.totalReturn.overall.value : undefined,
            lipperLeaderScores.response.preservation !== undefined ? lipperLeaderScores.response.preservation.overall.value : undefined,
          ];
        }

        for (let i: number = 0; i < labels.length; i++) {
          const field: ProposalFieldDAO = new ProposalFieldDAO(
            labels[i],
            values[i] !== undefined ? values[i] : '',
            ProposalStyleClassNames.ProposalTableContent,
          );

          field.cellType = values[i] !== undefined ? LipperScoreCell : NotApplicableCell;
          this.addFieldDAO(itemDAO, field);
        }
      }
    }
  }

  public getSeriesData(): SeriesData[][] {
    return this.getItemDAOs().map(itemDAO => [
      ...itemDAO.getSeriesData().map(data => ({
        ...data,
        key: itemDAO.item._id,
      })),
    ]);
  }

  public getColorsDict(): Dictionary<string> {
    return Object.fromEntries(this.getItemDAOs().map(({ item }) => [item._id, item.color ?? '']));
  }

  // private methods

  private addItemField(
    itemDAO: ComparisonItemDAO,
    name: string,
    value: string | number | undefined | null | { value: number },
    cellType: any,
    hasDisclaimer?: boolean,
    disclaimerNumber?: number,
    disclaimerKey?: string,
    sectionKey?: string,
    asteriskDisclaimerKey?: string,
  ): void {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = value != null ? cellType : NotApplicableCell;
    field.hasDisclaimer = hasDisclaimer;
    field.disclaimerNumber = disclaimerNumber;
    field.disclaimerKey = disclaimerKey;
    field.sectionKey = sectionKey;
    field.asteriskDisclaimerKey = asteriskDisclaimerKey;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(name, ProposalStyleClassNames.ProposalTableContent);
  }
}
